import React, { useState, useEffect } from "react";
import { Card, CardContent, Typography, TextField, Button, Box } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import SMSPreview from "./SMSPreview";

const TextMessageTemplatePreview = () => {
  const [templateName, setTemplateName] = useState("");
  const [templateFormat, setTemplateFormat] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [textBalance, setTextBalance] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  // Fetch user balance on component mount
  useEffect(() => {
    fetchUserBalance();
  }, []);

  const fetchUserBalance = () => {
    axios
      .get(`${BASE_URL}auth/view-profile`, {
        headers: {
          "Content-Type": "Application/json",
          version: "1.0.0",
          "x-access-token": `${token}`,
        },
      })
      .then((response) => {
        if (response.data.success) {
          const balance = response.data.data.text_balance || 0;
          setTextBalance(balance);
          if (balance === 0) {
            setShowWarning(true);
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Word count calculation
  const wordCount = templateFormat.trim() === "" ? 0 : templateFormat.trim().split(/\s+/).length;
  
  // Message count calculation (160 characters = 1 message)
  const messageCount = templateFormat.length === 0 ? 0 : Math.ceil(templateFormat.length / 160);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user has 0 credits
    if (textBalance === 0) {
      toast.error("Insufficient text credits. Please recharge your account to create text templates.");
      return;
    }

    if (!templateName || !templateFormat) {
      toast.error("Template Name and SMS Content are required.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BASE_URL}admin/text-template`,
        {
          templateName,
          templateType: "text",
          templateFormat,
        },
        {
          headers: {
            "x-access-token": token,
            version: "1.0.0",
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.success) {
        toast.success("Text template created successfully!");
        setTemplateName("");
        setTemplateFormat("");
      } else {
        toast.error(response.data.message || "Failed to create template");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create template");
    }
    setIsLoading(false);
  };

  return (
    <Box sx={{ 
      display: "flex", 
      gap: { xs: "10px", md: "20px" }, 
      width: "100%", 
      flexWrap: "wrap", 
      padding: { xs: "10px", sm: "15px", md: "20px" },
      boxSizing: "border-box",
      height: "100%",
      overflow: "hidden",
    }}>
      {/* Left Side - Form Controls */}
      <Card sx={{ 
        flex: { xs: "100%", md: 1 }, 
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)", 
        width: { xs: "100%", md: "auto" },
        maxWidth: "100%",
        overflow: "auto",
      }}>
        <CardContent sx={{ padding: { xs: "12px", sm: "14px", md: "16px" } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, mb: { xs: 1, md: 2 } }}>
            Text Template Configuration
          </Typography>

          {/* Credit Warning Banner */}
          {showWarning && (
            <Box sx={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: "6px",
              padding: "12px 15px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "10px"
            }}>
              <Box component="i" className="fa fa-exclamation-triangle" sx={{ color: "#856404", fontSize: "20px" }} />
              <Box>
                <Typography component="strong" sx={{ color: "#856404", fontWeight: 600, display: "block" }}>
                  Insufficient Credits
                </Typography>
                <Typography sx={{ margin: "5px 0 0 0", color: "#856404", fontSize: "14px" }}>
                  You have 0 text credits. Please recharge your account to create text templates.
                </Typography>
              </Box>
            </Box>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", gap: 2, mb: { xs: 2, md: 3 }, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                inputProps={{ maxLength: 100 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                fullWidth
                label="SMS Content"
                value={templateFormat}
                onChange={(e) => setTemplateFormat(e.target.value)}
                multiline
                minRows={4}
                // No maxLength restriction
                helperText={`${
                  templateFormat.length
                } characters | ${
                  templateFormat.trim().split(/\s+/).filter(Boolean).length
                } words | ${messageCount} message(s)`}
              />
            </Box>
            {/* <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Word count: {wordCount}
            </Typography> */}
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{ mt: 1 }}
              disabled={isLoading || textBalance === 0}
              style={textBalance === 0 ? { cursor: "not-allowed", opacity: 0.6 } : {}}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </Button>
          </form>
        </CardContent>
      </Card>
      {/* Right Side - Preview */}
      <Card sx={{ 
        flex: { xs: "100%", md: 1 }, 
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        width: { xs: "100%", md: "auto" },
        maxWidth: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}>
        <CardContent sx={{ 
          display: "flex", 
          flexDirection: "column",
          padding: { xs: "10px", sm: "12px", md: "16px" },
          flex: 1,
          overflow: "hidden",
        }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mb: { xs: 1, md: 2 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, mb: 0 }}>
              Preview
            </Typography>
          </Box>
          <Box sx={{ 
            width: "100%", 
            display: "flex", 
            justifyContent: "center",
            overflow: "visible",
            flex: 1,
          }}>
            <SMSPreview messageText={templateFormat} showInput={false} />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TextMessageTemplatePreview; 